import seedrandom from 'seedrandom';

import { V4, debug } from '@moleculeone/utils';

import { molTest, positionsDataTest } from '../checks';
import { addGroup, atomGroupKey, bondGroupKey } from './utils';
import { mergeGroupCycles } from './mergeGroupCycles';
import { mergeGroupTrees } from './mergeGroupTrees';
import { atomToNeighborsIndices, atomToNeighborsPairs } from '../selectors';
import { mergeComponents } from './mergeComponents';
import { addSpecialBondGroups } from './specialBondGroups';
import { DEFAULT_RANDOM_SEED } from '../const';
import { Mol } from '@moleculeone/types';

export function initialPositionsData(mol: Mol) {
  debug(molTest, mol);

  const groupGraph: any = {};
  for (const idx of Object.keys(mol.atoms)) {
    groupGraph[idx] = [];
  }

  const result = {
    mol,
    neighbors: atomToNeighborsPairs(mol),
    bondArrays: atomToNeighborsIndices(mol),
    groupGraph,
    groupPositions: {},
    treePositions: {},
    treeParams: {},
    result: {},
  };

  for (const bond of Object.values(mol.bonds)) {
    const positions: any = {};
    const groupKey = bondGroupKey(bond.idx);
    positions[bond.start] = V4.vect(0, 0);
    positions[bond.end] = V4.vect(100, 0);
    addGroup(result, groupKey, positions);
  }

  for (const atomIdx of Object.keys(mol.atoms)) {
    if (groupGraph[atomIdx].length === 0) {
      // create one element groups for atoms that are not in any other group
      // (as they have no bonds)
      const positions: any = {};
      const groupKey = atomGroupKey(atomIdx);
      positions[atomIdx] = V4.vect(0, 0);
      addGroup(result, groupKey, positions);
    }
  }

  return debug(positionsDataTest, result);
}

// positionsData represents process of constructing positions for a compound.
// groupGraph field is a bipartite graph: Atom index <-> Group of atom indices
// (that is, many-to-many relation). Each group of indices has their positions calculated.
// These positions are stored in groupPositions field.
// Groups may overlap, and they are merged until there's only one group left
// containing positions of all the atoms in the compound.
export function calculate(mol: Mol, params: { randomSeed?: number } = {}) {
  const { randomSeed = DEFAULT_RANDOM_SEED } = params;

  const random = seedrandom(String(randomSeed));

  try {
    const positionsData = initialPositionsData(mol);
    addSpecialBondGroups(positionsData);
    mergeGroupCycles(positionsData);
    mergeGroupTrees(random, positionsData, params);
    mergeComponents(positionsData);
    return positionsData.result;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(error.message);
    } else {
      throw new Error('Error in calculate function');
    }
  }
}
